/**
 * Esquemas de la API del **débito automático por archivo (PrismaNet)** — Cobranzas →
 * Débito automático (`docs/decisiones/debito-prismanet.md`, contrato en
 * `docs/api/contracts.md` § PrismaNet).
 *
 * Los archivos viajan en el cuerpo del pedido, **nunca** por una URL firmada a S3: la
 * planilla de presentación y sus TXT llevan el número completo de cada tarjeta, y no hay
 * ningún motivo para que ese archivo quede guardado en un bucket. Se lee, se pasa la
 * tarjeta a la bóveda cifrada y se descarta.
 */
import { z } from "zod";
import { PRISMANET_CARD_TYPES, PRISMANET_RESPONSE_KINDS } from "../prismanet.js";
import { dateOnly, objectId, pagination, reasonRequired, yearMonth } from "./base.js";
/** 1 MB de cuerpo (`BODY_LIMIT`): ~2.500 registros de presentación entran holgados. */
const fileContentBase64 = z.string().min(4).max(1_000_000);
/**
 * Importar la planilla de Maru: el Excel de presentación (.xlsx, hoja «Presentación») o el
 * TXT `DEBLI*` ya generado. Uno por marca: el tipo de tarjeta se elige al subir.
 * Sin `confirm` es el **ensayo en seco**: no escribe nada y devuelve el resumen.
 */
export const prismanetImportBody = z.object({
    cardType: z.enum(PRISMANET_CARD_TYPES),
    fileName: z.string().trim().min(1).max(200),
    contentBase64: fileContentBase64,
    confirm: z.boolean().default(false),
});
/** Revisión a mano de una tarjeta sin socio: asignarla o descartarla (descartar la purga). */
export const prismanetReviewBody = z.discriminatedUnion("action", [
    z.object({ action: z.literal("assign"), memberId: objectId }),
    z.object({ action: z.literal("discard"), reason: reasonRequired }),
]);
export const prismanetReviewQuery = pagination;
/**
 * Alta de una tarjeta para el débito por archivo (agregado del 16-oct). El número viaja una
 * sola vez, va a la bóveda cifrado y nunca vuelve. `posnetLast4` es obligatorio cuando el
 * primer mes se cobró por posnet: tiene que coincidir con la tarjeta cargada.
 */
export const prismanetEnrollBody = z
    .object({
    memberId: objectId,
    pan: z.string().trim().min(12).max(23),
    declaredKind: z.enum(["credit", "debit"]),
    expiry: z.object({
        month: z.number().int().min(1).max(12),
        year: z.number().int().min(2020).max(2100),
    }),
    holderName: z.string().trim().min(2).max(120),
    /** Vacío ⇒ el número de socio con ceros a la izquierda. */
    debitIdentifier: z
        .string()
        .trim()
        .regex(/^\d{1,15}$/, "El identificador del débito son hasta 15 dígitos")
        .optional(),
    firstMonthByPosnet: z.boolean().default(false),
    posnetLast4: z
        .string()
        .regex(/^\d{4}$/)
        .optional(),
    /** Versión del `legalDocVersions.debit_consent` que firma el socio (RN-PLA-23). */
    consentVersion: z.number().int().min(1),
    signatureMedium: z.enum(["app_authenticated", "kiosk_signature"]).default("kiosk_signature"),
})
    .refine((b) => !b.firstMonthByPosnet || b.posnetLast4 !== undefined, {
    message: "Si el primer mes se cobró por posnet, cargá los últimos 4 dígitos del cupón.",
    path: ["posnetLast4"],
});
/** Rango de la presentación. Vacío ⇒ desde la última presentación hasta hoy + N días. */
export const prismanetPresentationQuery = z.object({
    from: dateOnly.optional(),
    to: dateOnly.optional(),
});
export const prismanetPresentationBody = z.object({
    from: dateOnly.optional(),
    to: dateOnly.optional(),
});
export const prismanetPresentationsQuery = pagination.extend({
    status: z.enum(["generated", "uploaded", "closed", "voided"]).optional(),
});
export const prismanetPresentationStatusBody = z.discriminatedUnion("status", [
    z.object({ status: z.literal("uploaded") }),
    z.object({ status: z.literal("voided"), reason: reasonRequired }),
]);
/** Rendición bajada de PrismaNet: el TXT tal cual. Sin `kind` se reconoce por el nombre. */
export const prismanetResponseBody = z.object({
    fileName: z.string().trim().min(1).max(200),
    contentBase64: fileContentBase64,
    kind: z.enum(PRISMANET_RESPONSE_KINDS).optional(),
});
export const prismanetReportQuery = z.object({ month: yearMonth.optional() });
/** Etapa B: sin `confirm` devuelve qué pasaría (cuántas tarjetas, si está habilitado). */
export const prismanetTokenMigrationBody = z.object({
    confirm: z.boolean().default(false),
    reason: reasonRequired,
});
//# sourceMappingURL=prismanet.js.map